import { JSX, ReactNode, useRef, useState } from "react";
import { Col, Divider, Row } from "antd";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import TextArea from "../../../components/Input/TextArea";
import color from "../../../resource/color";
import image from "../../../resource/image";
import { ShopTierCompanyConfig } from "../../ShopTier/config";
import { ConfigTierBenefit } from "../../ShopTier/mockData";
import TierReviewList from "../TierReviewList";
import { Step2Result } from "./Step2";

interface Props {
  mode: "create" | "edit";
  config: ShopTierCompanyConfig;
  year: number;
  data: Step2Result | null;
  sourceTiers?: ConfigTierBenefit[];
  startFrom?: string;
  onBack: () => void;
  onConfirm: (reason: string) => void;
  /** While saving: the confirm button spins and both buttons are locked. */
  loading?: boolean;
}

function SectionReview({
  title,
  count,
  unit,
  children,
}: {
  title: string;
  count: number;
  unit: string;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <Text fontWeight={700} fontSize={16} color="Text2">
          {title}
        </Text>
        <Text fontSize={13} color="Text3">
          {count} {unit}
        </Text>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

function ReviewBox({ children }: { children: ReactNode }) {
  return (
    <div style={{ background: color.background1, borderRadius: 8, padding: "12px 14px" }}>
      {children}
    </div>
  );
}

function Step3({ mode, config, year, data, sourceTiers = [], startFrom, onBack, onConfirm, loading = false }: Props): JSX.Element {
  const [reason, setReason] = useState("");
  // Required in edit mode only; the error shows after the first confirm press (same as Step2).
  const [showReasonError, setShowReasonError] = useState(false);
  const reasonRef = useRef<HTMLDivElement>(null);
  const isReasonMissing = mode === "edit" && reason.trim() === "";

  if (!data) {
    return (
      <div style={{ marginTop: 24 }}>
        <Text fontSize={14} color="Text3">ยังไม่มีข้อมูลให้ตรวจสอบ กรุณากลับไปกรอกข้อมูลในขั้นตอนก่อนหน้า</Text>
        <Divider />
        <Row justify="end">
          <Col>
            <Button typeButton="border-primary" title="ย้อนกลับ" style={{ width: "auto" }} onClick={onBack} />
          </Col>
        </Row>
      </div>
    );
  }

  const totalBenefits = data.tiers.reduce((sum, t) => sum + t.benefits.length, 0);
  const bannerLabel = data.bannerUrl ? "อัปโหลดเอง" : "ภาพจากทางบริษัท";
  const showDiff = mode === "edit" || startFrom === "copy";

  return (
    <div style={{ marginTop: 24 }}>
      <Row justify="space-between" wrap={false} gutter={16}>
        <Col>
          <Text level={3} fontWeight={700} color="Text2">
            ชุดสิทธิประโยชน์ปี {year}
          </Text>
          <div style={{ marginTop: 4 }}>
            <Text fontSize={14} color="Text3">
              {config.tiers.length} ระดับ · {totalBenefits} สิทธิประโยชน์ · แบนเนอร์: {bannerLabel}
            </Text>
          </div>
        </Col>
        <Col flex="none">
          <img
            src={data.bannerUrl || image.tierBanner}
            alt="แบนเนอร์"
            style={{ width: 230, height: 170, objectFit: "cover", borderRadius: 12, border: `1px solid ${color.border}` }}
          />
        </Col>
      </Row>
      <Divider />

      <TierReviewList
        config={config}
        tiers={data.tiers}
        cardImageOverrides={data.cardImageOverrides}
        cardBackgroundOverrides={data.cardBackgroundOverrides}
        colorOverrides={data.colorOverrides}
        originalTiers={showDiff ? sourceTiers : []}
        changedTierKeys={showDiff ? data.changedTierKeys : []}
      />

      <SectionReview title="วิธีการทำงาน" count={data.cards.length} unit="การ์ด">
        {data.cards.map((c, idx) => (
          <ReviewBox key={idx}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {c.iconUrl && (
                <img src={c.iconUrl} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
              )}
              <div>
                <Text fontWeight={700} fontSize={14} color="Text2">{c.title || "-"}</Text>
                {c.description && (
                  <div style={{ marginTop: 2 }}>
                    <Text fontSize={13} color="Text3">{c.description}</Text>
                  </div>
                )}
              </div>
            </div>
          </ReviewBox>
        ))}
      </SectionReview>

      <SectionReview title="คำถามที่พบบ่อย" count={data.faqs.length} unit="คำถาม">
        {data.faqs.map((f, idx) => (
          <ReviewBox key={idx}>
            <Text fontWeight={700} fontSize={14} color="Text2">{f.question || "-"}</Text>
            {f.answer && (
              <div style={{ marginTop: 2 }}>
                <Text fontSize={13} color="Text3">{f.answer}</Text>
              </div>
            )}
          </ReviewBox>
        ))}
      </SectionReview>

      <SectionReview title="กติกาและเงื่อนไข" count={data.rules.length} unit="หัวข้อ">
        {data.rules.map((r, idx) => (
          <ReviewBox key={idx}>
            <Text fontWeight={700} fontSize={14} color="Text2">{r.title || "-"}</Text>
            <div
              style={{ marginTop: 4, fontFamily: "Sarabun", fontSize: 13, color: color.Text3, lineHeight: "20px" }}
              dangerouslySetInnerHTML={{ __html: r.content }}
            />
          </ReviewBox>
        ))}
      </SectionReview>

      {mode === "edit" && (
        <div>
          <div style={{ marginBottom: 8 }}>
            <Text fontSize={14} fontWeight={500} color="Text2">เหตุผลของการเปลี่ยนแปลง</Text>
          </div>
          <div ref={reasonRef}>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น แก้คำผิดของสิทธิ์ทัวร์ประจำปีตามที่ร้านค้าแจ้ง"
              status={showReasonError && isReasonMissing ? "error" : undefined}
            />
          </div>
          {showReasonError && isReasonMissing && (
            <div style={{ marginTop: 4 }}>
              <Text fontFamily="Sarabun" fontSize={14} color="error">กรุณาระบุเหตุผล</Text>
            </div>
          )}
        </div>
      )}

      <Divider />
      <Row justify="start" gutter={8}>
        <Col>
          <Button
            typeButton={loading ? "disabled" : "border-primary"}
            title="ย้อนกลับ"
            style={{ width: "auto" }}
            disabled={loading}
            onClick={onBack}
          />
        </Col>
        <Col>
          <Button
            typeButton="primary"
            title={mode === "edit" ? "ยืนยันการแก้ไข" : "ยืนยันการสร้าง"}
            style={{ width: "auto" }}
            loading={loading}
            onClick={() => {
              if (loading) return;
              if (isReasonMissing) {
                setShowReasonError(true);
                reasonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                reasonRef.current?.querySelector("textarea")?.focus({ preventScroll: true });
                return;
              }
              onConfirm(reason.trim());
            }}
          />
        </Col>
      </Row>
    </div>
  );
}

export default Step3;
